
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useHasPermission, usePermissions } from '@contexts/permissions-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { AccessPassPageProps } from './AccessPassPage.types';

/**
 * AccessPassPage - MFE wrapper for AccessPass
 *
 * This feature loads the remote AccessPass Page MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in Client profile menu:
 * 1. User must be logged in
 * 2. FeatureFlagGuard - accessPassMfeEnabled must be enabled
 * 3. PermissionGuard - AccessPassWithTraining_View_Own required, AccessPassNoTraining_View_Own
 *
 */
const AccessPassPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const isVisitor = useHasPermission('AccessPassNoTraining_View_Own');
  const isTrainee = useHasPermission('AccessPassWithTraining_View_Own');
  const navigate = useNavigate();

  const navigateToCheckin = React.useCallback((trainingId: string): void => {
    navigate(`/train/checkin/${trainingId}`);
  }, [navigate]);

  const navigateToHome = React.useCallback((): void => {
    if (isVisitor) {
      navigate(APP_ROUTES.HOME);
      return;
    }

    if (isTrainee) {
      navigate(APP_ROUTES.TRAIN_OVERVIEW);
      return;
    }

    navigate(APP_ROUTES.HOME);
  }, [isVisitor, isTrainee, navigate]);

  const [RemoteComponent, setRemoteComponent] =
    React.useState<React.ComponentType<AccessPassPageProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadComponent = async (): Promise<void> => {
      try {
        const { default: Component } =
          await import('access_pass_mfe/AccessPass');
        setRemoteComponent(() => Component);
      } catch (error) {
        console.error('Error loading AccessPass page:', error);
        setLoadError(error as Error);
      }
    };

    loadComponent();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteComponent) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteComponent
        msalInstance={instance}
        user={authData}
        permissions={permissions}
        onNavigateHome={navigateToHome}
        onNavigateToCheckin={navigateToCheckin}
      />
    </React.Suspense>
  );
});

AccessPassPage.displayName = 'AccessPassPage';

export { AccessPassPage };

